'use client';
import { useInfiniteQuery } from '@tanstack/react-query';

import { sportsApi } from '@/services/api';
import type { CommentQueryParams, CommentsResponse } from '@/types';

export const SPORTS_COMMENTS_QUERY_KEYS = {
  all: ['sports-comments'] as const,
  list: (params: CommentQueryParams) =>
    [...SPORTS_COMMENTS_QUERY_KEYS.all, 'list', params] as const,
};

const getCommentOrderParams = (
  sortBy: string
): { order: 'createdAt' | 'reactionCount'; ascending: boolean } => {
  if (sortBy === 'oldest') {
    return { order: 'createdAt', ascending: true };
  }
  if (sortBy === 'popular') {
    return { order: 'reactionCount', ascending: false };
  }
  return { order: 'createdAt', ascending: false };
};

export const useSportsCommentsInfinite = (
  eventId: string | number,
  sortBy: string,
  holdersOnly: boolean
) => {
  const { order, ascending } = getCommentOrderParams(sortBy);

  const params: CommentQueryParams = {
    parent_entity_id: eventId,
    parent_entity_type: 'Series',
    limit: 5,
    order,
    ascending,
    holders_only: holdersOnly,
    get_positions: true,
    get_reports: true,
  };

  return useInfiniteQuery<CommentsResponse>({
    queryKey: SPORTS_COMMENTS_QUERY_KEYS.list(params),
    queryFn: ({ pageParam = 0 }) =>
      sportsApi.getCommentsPaginated({
        ...params,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.meta.offset + lastPage.meta.limit;
      return lastPage.data.length === lastPage.meta.limit ? nextOffset : undefined;
    },
    enabled: !!eventId,
  });
};
